import { Input, Option, Select, Textarea } from '@material-tailwind/react';
import { ArrowLeft, RefreshCwIcon, SendIcon } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAddSupportTicket } from '../../custom-hooks/use-support';
import { SUPPORT_TICKET_PRIORITIES_ENUM } from '../../utils/enums';
import { URLS } from '../../router-x/routes';
import { useAuth } from '../../services/contexts/authContext';
import { useGetSupportCategories } from '../../custom-hooks/use-support-categories';
import { AttachmentPicker } from '../../components';

const INITIAL_STATE = {
    subject: '',
    description: '',
    category: '',
    priority: 'medium',
    attachments: []
}

// Must mirror the backend limits in wb-bas-backend-main/models/SupportTicket.js
// (subject: 200, description: 5000). Enforced here via maxLength so the customer
// can never type past the limit and the backend validation error is never hit.
const SUBJECT_MAX_LENGTH = 200;
const DESCRIPTION_MAX_LENGTH = 5000;

function CustomerSupportCrudForm() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState(INITIAL_STATE);
    const addSupportTicketMutation = useAddSupportTicket();
    const { data: categories = [], isLoading: categoriesLoading } = useGetSupportCategories();
    const [submitLoading, setSubmitLoading] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSelectChange = (name, value) => {
        setFormData({ ...formData, [name]: value });
    };

    // Picking a category pre-fills the priority from that category's default (SRD §4.3),
    // which the member can still override. Without this the form always posted a
    // priority, and the backend — which only falls back to the category default when
    // none was sent — never got to apply it. A safety issue would have landed as Medium.
    const handleCategoryChange = (slug) => {
        const picked = categories.find((c) => c.slug === slug);
        setFormData((prev) => ({
            ...prev,
            category: slug,
            priority: picked?.defaultPriority || prev.priority,
        }));
    };

    const { userDetails } = useAuth();
    const baseUrl = userDetails?.role === 'team_member' ? URLS.TEAM_MEMBER.SUPPORT : URLS.CUSTOMER.SUPPORT;

    const handleSubmit = async () => {
        setSubmitLoading(true);
        try {
            await addSupportTicketMutation.mutateAsync(formData);
            navigate(baseUrl, { state: { refreshTickets: true } });
        } catch (error) {
            console.error("Error adding support ticket:", error);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleNavigationBack = () => {
        navigate(baseUrl);
    };

    return (
        <div className="animate-fadeIn px-4 py-5 md:px-[34px] md:py-[30px]">
            <div className="mx-auto max-w-3xl">
                <button
                    onClick={handleNavigationBack}
                    className="mb-4 inline-flex items-center gap-2 rounded-[11px] border-[1.5px] border-[#E6DDCF] bg-white px-3.5 py-2 font-display text-[13px] font-semibold text-[#3B2A1E] transition-colors hover:bg-cream"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Tickets
                </button>
                <div className="mb-6">
                    <h1 className="font-display text-[25px] font-bold text-ink">Create Support Ticket</h1>
                    <p className="mt-1 text-sm text-[#8C8175]">Describe your issue and we&rsquo;ll help you resolve it.</p>
                </div>

                <div className="rounded-2xl border border-[#ECE3D5] bg-white p-6 md:p-8">
                    <div className="space-y-6">
                        <div>
                            <Input
                                label="Subject *"
                                size="lg"
                                value={formData.subject}
                                name="subject"
                                onChange={handleInputChange}
                                maxLength={SUBJECT_MAX_LENGTH}
                            />
                            <div className={`mt-1 text-right text-xs ${formData.subject.length >= SUBJECT_MAX_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
                                {formData.subject.length} / {SUBJECT_MAX_LENGTH}
                            </div>
                        </div>

                        <div>
                            <Textarea
                                label="Description *"
                                value={formData.description}
                                name="description"
                                onChange={handleInputChange}
                                rows={6}
                                maxLength={DESCRIPTION_MAX_LENGTH}
                            />
                            <div className={`mt-1 text-right text-xs ${formData.description.length >= DESCRIPTION_MAX_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
                                {formData.description.length} / {DESCRIPTION_MAX_LENGTH}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                            <div className="md:col-span-2">
                                <Select
                                    label="Category *"
                                    value={formData.category}
                                    onChange={handleCategoryChange}
                                    disabled={categoriesLoading}
                                    selected={() => categories.find((c) => c.slug === formData.category)?.name || ''}
                                >
                                    {categories.map((category) => (
                                        <Option key={category.slug} value={category.slug}>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium">{category.name}</span>
                                                {category.description && (
                                                    <span className="text-xs text-[#8C8175]">{category.description}</span>
                                                )}
                                            </div>
                                        </Option>
                                    ))}
                                </Select>
                            </div>

                            <div>
                                <Select
                                    label="Priority *"
                                    value={formData.priority}
                                    onChange={(value) => handleSelectChange('priority', value)}
                                >
                                    {SUPPORT_TICKET_PRIORITIES_ENUM.map((level) => (
                                        <Option key={level} value={level}>
                                            <span className="capitalize">{level}</span>
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        </div>

                        <div>
                            <p className="mb-2 text-sm font-medium text-[#3B2A1E]">Attachments</p>
                            <AttachmentPicker
                                attachments={formData.attachments}
                                onChange={(attachments) => setFormData((prev) => ({ ...prev, attachments }))}
                                disabled={submitLoading}
                                label="Add photos or documents"
                            />
                        </div>

                        <div className="flex items-center justify-end gap-3 border-t border-[#F1E9DC] pt-6">
                            <button
                                onClick={handleNavigationBack}
                                disabled={submitLoading}
                                className="rounded-xl border-[1.5px] border-[#E6DDCF] bg-white px-5 py-2.5 font-display text-sm font-semibold text-[#3B2A1E] transition-colors hover:bg-cream disabled:opacity-60"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={!formData.subject || !formData.description || !formData.category || submitLoading}
                                className="flex items-center gap-2 rounded-xl bg-brand px-5 py-2.5 font-display text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {submitLoading ? <RefreshCwIcon className="h-4 w-4 animate-spin" /> : <SendIcon className="h-4 w-4" />}
                                Submit Ticket
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CustomerSupportCrudForm;
